import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type {
  SessionWithSla,
  SessionFilters,
  InboxStats,
  SupportSessionUpdate,
  ResolveSessionPayload,
  AssignAgentPayload,
  ChangePriorityPayload,
  ChangeStatusPayload,
  SessionStatus,
  DemandType,
} from '@/types/support.types';
import type { Database } from '@/integrations/supabase/types';

type DbSessionStatus = Database['public']['Enums']['support_session_status'];
type DbDemandType = Database['public']['Enums']['demand_type'];
type DbResolutionType = Database['public']['Enums']['resolution_type'];

const QUERY_KEY = 'support-sessions';

// ============================================
// Hook Principal
// ============================================

export function useSupportInbox(filters?: SessionFilters) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query para buscar sessões da view
  const sessionsQuery = useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async (): Promise<SessionWithSla[]> => {
      let query = supabase
        .from('v_sessions_with_sla')
        .select('*');

      // Default ordering; will be overridden if computed_queue filtering is used
      let useDefaultOrder = true;

      // Aplicar filtros
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status as DbSessionStatus);
      } else {
        // Sem filtro explícito: trazer apenas status ativos.
        // Evita que o corte de 1000 linhas do PostgREST seja consumido
        // por sessões resolvidas/arquivadas e esconda chamados antigos.
        query = query.in('status', [
          'new',
          'in_progress',
          'waiting_client',
          'waiting_internal',
          'pre_ticket',
          'awaiting_classification',
        ] as DbSessionStatus[]);
      }


      if (filters?.queue) {
        // Support both 'queue' column and 'computed_queue' column
        if (filters.queue.startsWith('queue_')) {
          query = query.eq('computed_queue', filters.queue);
        } else {
          query = query.eq('queue', filters.queue);
        }
      }

      if (filters?.demand_type) {
        query = query.eq('demand_type', filters.demand_type as DbDemandType);
      }

      if (filters?.priority) {
        query = query.eq('priority', filters.priority);
      }

      if (filters?.assigned_to) {
        if (filters.assigned_to === 'unassigned') {
          query = query.is('assigned_to', null);
        } else if (filters.assigned_to !== 'all') {
          query = query.eq('assigned_to', filters.assigned_to);
        }
      }

      if (filters?.sla_status) {
        query = query.eq('sla_status', filters.sla_status);
      }

      if (filters?.search) {
        const words = filters.search.trim().split(/\s+/).filter(Boolean);
        for (const word of words) {
          query = query.or(
            `contact_name.ilike.%${word}%,contact_phone.ilike.%${word}%,contact_first_name.ilike.%${word}%,contact_last_name.ilike.%${word}%`
          );
        }
      }

      if (filters?.inbox_channel) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        query = (query as any).eq('inbox_channel', filters.inbox_channel);
      }

      // Apply ordering
      query = query.order('created_at', { ascending: false }).limit(2000);


      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as SessionWithSla[];
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 5 * 60 * 1000, // Fallback: refetch every 5 minutes
  });

  // Realtime: atualizar lista quando sessões mudam
  useEffect(() => {
    const channel = supabase
      .channel('support-sessions-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_sessions' },
        () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
          queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
          queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'support_sessions' },
        () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
          queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
          queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Calcular estatísticas a partir dos dados
  const stats: InboxStats = {
    total_new: sessionsQuery.data?.filter(s => s.status === 'new').length || 0,
    total_in_progress: sessionsQuery.data?.filter(s => s.status === 'in_progress').length || 0,
    total_waiting: sessionsQuery.data?.filter(s => s.status === 'waiting_client' || s.status === 'waiting_internal').length || 0,
    unassigned: sessionsQuery.data?.filter(s => !s.assigned_to && s.status !== 'resolved').length || 0,
    breached_sla: sessionsQuery.data?.filter(s => s.sla_status === 'breached').length || 0,
    warning_sla: sessionsQuery.data?.filter(s => s.sla_status === 'warning').length || 0,
    avg_response_time_min: calculateAvgResponseTime(sessionsQuery.data),
  };

  // Mutation: Atribuir agente
  const assignAgentMutation = useMutation({
    mutationFn: async ({ session_id, agent_id }: AssignAgentPayload) => {
      const { error } = await supabase
        .from('support_sessions')
        .update({ 
          assigned_to: agent_id,
          assigned_at: agent_id ? new Date().toISOString() : null,
        })
        .eq('id', session_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    },
  });

  // Mutation: Alterar prioridade
  const changePriorityMutation = useMutation({
    mutationFn: async ({ session_id, priority }: ChangePriorityPayload) => {
      const { error } = await supabase
        .from('support_sessions')
        .update({ priority })
        .eq('id', session_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    },
  });

  // Mutation: Alterar status
  const changeStatusMutation = useMutation({
    mutationFn: async ({ session_id, status }: ChangeStatusPayload) => {
      const { error } = await supabase
        .from('support_sessions')
        .update({ status: status as DbSessionStatus })
        .eq('id', session_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    },
  });

  // Mutation: Resolver sessão
  const resolveSessionMutation = useMutation({
    mutationFn: async ({ session_id, resolution_type, resolution_notes }: ResolveSessionPayload) => {
      const { error } = await supabase
        .from('support_sessions')
        .update({
          status: 'resolved' as DbSessionStatus,
          resolved_at: new Date().toISOString(),
          resolution_type: resolution_type as DbResolutionType,
          resolution_notes,
        })
        .eq('id', session_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    },
  });

  // Mutation: Reabrir sessão
  const reopenSessionMutation = useMutation({
    mutationFn: async (session_id: string) => {
      const { error } = await supabase
        .from('support_sessions')
        .update({
          status: 'in_progress' as DbSessionStatus,
          resolved_at: null,
          resolution_type: null,
          resolution_notes: null,
        })
        .eq('id', session_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    },
  });

  return {
    // Query state
    sessions: sessionsQuery.data || [],
    isLoading: sessionsQuery.isLoading,
    isError: sessionsQuery.isError,
    error: sessionsQuery.error,
    refetch: sessionsQuery.refetch,

    // Stats
    stats,

    // Mutations
    assignAgent: assignAgentMutation.mutateAsync,
    isAssigning: assignAgentMutation.isPending,

    changePriority: changePriorityMutation.mutateAsync,
    isChangingPriority: changePriorityMutation.isPending,

    changeStatus: changeStatusMutation.mutateAsync,
    isChangingStatus: changeStatusMutation.isPending,

    resolveSession: resolveSessionMutation.mutateAsync,
    isResolving: resolveSessionMutation.isPending,

    reopenSession: reopenSessionMutation.mutateAsync,
    isReopening: reopenSessionMutation.isPending,
  };
}

// ============================================
// Hook para sessão individual
// ============================================

export function useSupportSession(sessionId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEY, 'single', sessionId],
    queryFn: async (): Promise<SessionWithSla | null> => {
      if (!sessionId) return null;

      const { data, error } = await supabase
        .from('v_sessions_with_sla')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) throw error;
      return data as SessionWithSla;
    },
    enabled: !!user && !!sessionId,
    staleTime: 30 * 1000,
  });
}

// ============================================
// Helpers
// ============================================

function calculateAvgResponseTime(sessions?: SessionWithSla[]): number | null {
  if (!sessions || sessions.length === 0) return null;

  const respondedSessions = sessions.filter(
    s => s.minutes_to_first_response !== null
  );

  if (respondedSessions.length === 0) return null;

  const total = respondedSessions.reduce(
    (sum, s) => sum + (s.minutes_to_first_response || 0),
    0
  );

  return Math.round(total / respondedSessions.length);
}

// ============================================
// Constantes para UI
// ============================================

export const SLA_STATUS_CONFIG = {
  ok: { label: 'OK', color: 'bg-green-500' },
  warning: { label: 'Atenção', color: 'bg-yellow-500' },
  breached: { label: 'Violado', color: 'bg-red-500' },
  responded: { label: 'Respondido', color: 'bg-blue-500' },
  no_sla: { label: 'Sem SLA', color: 'bg-muted' },
} as const;

export const SESSION_STATUS_CONFIG: Record<SessionStatus, { label: string; color: string }> = {
  new: { label: 'Nova', color: 'bg-blue-500' },
  in_progress: { label: 'Em Andamento', color: 'bg-green-500' },
  waiting_client: { label: 'Aguardando Cliente', color: 'bg-yellow-500' },
  waiting_internal: { label: 'Aguardando Interno', color: 'bg-orange-500' },
  resolved: { label: 'Resolvida', color: 'bg-slate-400' },
  pre_ticket: { label: 'Pré-chamado', color: 'bg-indigo-400' },
  awaiting_classification: { label: 'Aguardando Classificação', color: 'bg-violet-500' },
  auto_archived: { label: 'Arquivado Auto', color: 'bg-gray-400' },
};

export const DEMAND_TYPE_CONFIG: Record<DemandType, { label: string; color: string }> = {
  status: { label: 'Status', color: 'bg-blue-500' },
  documents: { label: 'Documentos', color: 'bg-purple-500' },
  doubt: { label: 'Dúvida', color: 'bg-cyan-500' },
  financial: { label: 'Financeiro', color: 'bg-green-500' },
  processes: { label: 'Processos', color: 'bg-amber-500' },
  complaints: { label: 'Reclamações', color: 'bg-red-500' },
};

export const PRIORITY_CONFIG = {
  normal: { label: 'Normal', color: 'bg-blue-500' },
  urgent: { label: 'Urgente', color: 'bg-red-500' },
} as const;
